import { DatasheetElement, DatasheetElementId } from "../types/datasheet-element";
import type { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { FieldType } from "@common/dto/field/type";

type TabId = string | number;

export function isServerDerivedDatasheetField(
  field:
    | {
        fieldtype?: { typeid?: number; computed?: boolean };
        type?: number;
        rawFieldType?: number;
        calcField?: boolean;
        computed?: boolean;
      }
    | undefined
): boolean {
  if (!field) return false;
  const typeId = field.fieldtype?.typeid ?? field.type ?? field.rawFieldType;
  if (
    typeId === FieldType.computed
    || typeId === FieldType.summary
    || typeId === FieldType.tableSummary
  ) {
    return true;
  }
  if (field.calcField === true || field.computed === true) {
    return true;
  }
  if (field.fieldtype?.computed === true) {
    return true;
  }
  return false;
}

function historySnapshotDisplayString(el: DatasheetElement<DatasheetFieldComponentGetDto>): string {
  const orig = el.original as { displayValue?: unknown; field?: { name?: unknown; value?: unknown } };
  const f = orig?.field;
  return String(orig?.displayValue ?? f?.name ?? f?.value ?? "");
}

export type DatasheetStorageTabs = Record<TabId, Record<DatasheetElementId, DatasheetElement>>;

type DatasheetElementsHistoryRecordId = string;
type TabHistory = {
  sequence: Array<DatasheetElementsHistoryRecordId>;
  elements: Record<DatasheetElementsHistoryRecordId, Record<DatasheetElementId, DatasheetElement>>;
  currentHistoryRecordId: DatasheetElementsHistoryRecordId;
};
type TabsHistory = Record<TabId, TabHistory>;

export class DatasheetStorage {
  private tabs: DatasheetStorageTabs = {};
  private history: TabsHistory = {};

  addTab(tabId: TabId) {
    // Only initialize when the tab is new; avoid wiping existing elements
    if (this.tabs[tabId] === undefined) {
      this.tabs[tabId] = {};
    }
  }

  removeTab(tabId: TabId) {
    delete this.tabs[tabId];
  }

  setElements(tabId: TabId, elements: Record<DatasheetElementId, DatasheetElement>) {
    this.tabs[tabId] = { ...elements };
  }

  updateElement(tabId: TabId, el: DatasheetElement) {
    this.tabs[tabId]![el.id] = el;
  }

  removeElement(tabId: TabId, el: DatasheetElement) {
    delete this.tabs[tabId]![el.id];
  }

  getElements(tabId: TabId) {
    return this.tabs[tabId];
  }

  updateHistoryRecord(
    params: {
      tabId: TabId;
      historyRecordId: DatasheetElementsHistoryRecordId;
      elements: Record<DatasheetElementId, DatasheetElement>
    }
  ) {
    const { tabId, historyRecordId, elements } = params;

    const tabHistory = this.history[tabId];

    if (tabHistory) {
      tabHistory.elements[historyRecordId] = elements;
    }
  }

  addHistoryRecord(
    params: {
      tabId: TabId,
      currentHistoryRecordId: string | undefined,
      newHistoryRecordId: string,
      elementsForNewHistoryRecord: Record<DatasheetElementId, DatasheetElement>,
    }
  ) {
    const { tabId, currentHistoryRecordId, newHistoryRecordId, elementsForNewHistoryRecord } = params;

    const tabHistory = this.history[tabId];

    if (tabHistory && currentHistoryRecordId) {
      const currentHistoryIndex = tabHistory.sequence.indexOf(currentHistoryRecordId);
      const effectiveIndex =
        currentHistoryIndex >= 0 ? currentHistoryIndex : tabHistory.sequence.length - 1;

      const historyRecordsToDelete =
        effectiveIndex >= 0 ? tabHistory.sequence.slice(effectiveIndex + 1) : [];

      historyRecordsToDelete.forEach((historyRecordId) => {
        delete tabHistory.elements[historyRecordId];
      });

      tabHistory.sequence = (effectiveIndex >= 0
        ? tabHistory.sequence.slice(0, effectiveIndex + 1)
        : []
      ).concat(newHistoryRecordId);

      tabHistory.elements[newHistoryRecordId] = elementsForNewHistoryRecord;

      return;
    }

    this.history[tabId] = {
      sequence: [newHistoryRecordId],
      elements: {
        [newHistoryRecordId]: elementsForNewHistoryRecord,
      },
      currentHistoryRecordId: newHistoryRecordId,
    };
  }

  setCurrentHistoryRecordId(tabId: TabId, historyRecordId: DatasheetElementsHistoryRecordId) {
    const tabHistory = this.history[tabId];

    if (tabHistory) {
      tabHistory.currentHistoryRecordId = historyRecordId;
    }
  }

  getCurrentHistoryRecordId(tabId: TabId) {
    const tabHistory = this.history[tabId];

    return tabHistory?.currentHistoryRecordId;
  }

  getHistoryRecord(tabId: TabId, historyRecordId: DatasheetElementsHistoryRecordId) {
    const tabHistory = this.history[tabId];

    return tabHistory?.elements[historyRecordId];
  }

  getHistorySequence(tabId: TabId) {
    const tabHistory = this.history[tabId];

    return tabHistory?.sequence;
  }

  deleteHistory(tabId: TabId) {
    delete this.history[tabId];
  }

  mergeComputedFieldsIntoPreviousHistoryRecord(
    tabId: TabId,
    currentHistoryRecordId: DatasheetElementsHistoryRecordId,
    tabElements: Record<DatasheetElementId, DatasheetElement>
  ) {
    const tabHistory = this.history[tabId];
    if (!tabHistory?.sequence.length) return;

    const currentIndex = tabHistory.sequence.indexOf(currentHistoryRecordId);
    if (currentIndex <= 1) return; // No previous, or previous is initial state (don't merge into init)

    const previousRecordId = tabHistory.sequence[currentIndex - 1];
    if (!previousRecordId) return;
    const record = tabHistory.elements[previousRecordId];
    if (!record) return;

    const computedElementIds: DatasheetElementId[] = [];
    for (const id of Object.keys(tabElements) as DatasheetElementId[]) {
      const el = tabElements[id];
      const fieldOriginal = el?.type === 'field' ? (el as DatasheetElement<DatasheetFieldComponentGetDto>).original?.field : undefined;
      if (fieldOriginal && isServerDerivedDatasheetField(fieldOriginal)) {
        computedElementIds.push(id);
      }
    }
    if (computedElementIds.length === 0) return;

    let changed = false;
    const merged = { ...record };
    for (const id of computedElementIds) {
      const src = tabElements[id];
      if (src) {
        merged[id] = { ...src };
        changed = true;
      }
    }
    if (changed) {
      tabHistory.elements[previousRecordId] = merged;
    }
  }

  patchServerDerivedFieldsIntoHistoryRecord(
    tabId: TabId,
    historyRecordId: DatasheetElementsHistoryRecordId,
    tabElements: Record<DatasheetElementId, DatasheetElement>
  ): void {
    const tabHistory = this.history[tabId];
    if (!tabHistory?.sequence.length) return;

    const record = tabHistory.elements[historyRecordId];
    if (!record) return;

    let changed = false;
    const next = { ...record };
    for (const id of Object.keys(tabElements) as DatasheetElementId[]) {
      const el = tabElements[id];
      if (el?.type !== 'field') continue;
      const fieldOriginal = (el as DatasheetElement<DatasheetFieldComponentGetDto>).original?.field;
      if (!fieldOriginal || !isServerDerivedDatasheetField(fieldOriginal)) continue;

      const newVal = historySnapshotDisplayString(el as DatasheetElement<DatasheetFieldComponentGetDto>);
      const curEl = record[id];
      const curVal =
        curEl?.type === 'field'
          ? historySnapshotDisplayString(curEl as DatasheetElement<DatasheetFieldComponentGetDto>)
          : '';
      if (newVal !== curVal) {
        next[id] = { ...el };
        changed = true;
      }
    }
    if (changed) {
      tabHistory.elements[historyRecordId] = next;
    }
  }
}